/**
 * Phase 3.5 tile stats — locked concrete ranges from GO qualitative anchors:
 * - movement: mountains cost more MP than open plains
 * - food: plains / grasslands / rivers higher
 * - defense: bonus or penalty per biome
 * - rivers: impassable in Spring floods unless a bridge
 *
 * Magnitudes were open on paper; these values are the ship lock for 0.3.5.
 */
import type { Season } from "./clock.ts";
import type { Biome } from "./map.ts";

/** Per-biome tactical / economic profile. */
export interface TileStatProfile {
  /** Movement points spent to *enter* this hex (∞ = impassable base). */
  moveCost: number;
  /** Seasonal food yield range when the tile is claimed/worked (inclusive). */
  foodRange: readonly [min: number, max: number];
  /**
   * Defense modifier for fights on this ground.
   * Positive = defender advantage; negative = exposed.
   */
  defenseMod: number;
  /** Base land passability ignoring season/bridge rules. */
  passable: boolean;
  /**
   * When true, Spring floods block entry unless the cell has a bridge.
   * Only river biomes use this.
   */
  springFloodBlocksUnlessBridge: boolean;
  /** Short chrome label for HUD. */
  note: string;
}

/**
 * Locked table — all biomes from map.ts Biome union.
 * plains ≈ grasslands (no separate grassland biome in generator).
 */
export const TILE_STATS: Record<Biome, TileStatProfile> = {
  plains: {
    moveCost: 1,
    foodRange: [3, 5],
    defenseMod: 0,
    passable: true,
    springFloodBlocksUnlessBridge: false,
    note: "Open ground — easy march, strong farms",
  },
  forest: {
    moveCost: 2,
    foodRange: [1, 3],
    defenseMod: 1,
    passable: true,
    springFloodBlocksUnlessBridge: false,
    note: "Timber cover — slower, mild defense · +5–10 wood when claimed",
  },
  hills: {
    moveCost: 2,
    foodRange: [1, 2],
    defenseMod: 2,
    passable: true,
    springFloodBlocksUnlessBridge: false,
    note: "High ground — defend well · +1–2 food · +1–2 wood when claimed",
  },
  foothills: {
    moveCost: 2,
    foodRange: [1, 2],
    defenseMod: 1,
    passable: true,
    springFloodBlocksUnlessBridge: false,
    note: "Approach to peaks · +1–2 food · +1–2 wood when claimed",
  },
  mountains: {
    moveCost: 4,
    foodRange: [0, 1],
    defenseMod: 3,
    passable: true,
    springFloodBlocksUnlessBridge: false,
    note: "Hard climb — high MP, strong defense, scarce food",
  },
  marsh: {
    moveCost: 3,
    foodRange: [1, 2],
    defenseMod: -1,
    passable: true,
    springFloodBlocksUnlessBridge: false,
    note: "Bog — slow and exposed",
  },
  coast: {
    moveCost: 1,
    foodRange: [2, 3],
    defenseMod: 0,
    passable: true,
    springFloodBlocksUnlessBridge: false,
    note: "Shore — fair food, open march",
  },
  river: {
    moveCost: 2,
    foodRange: [3, 5],
    defenseMod: -1,
    passable: true,
    springFloodBlocksUnlessBridge: true,
    note: "Fertile banks — Spring flood blocks without a bridge",
  },
  water: {
    moveCost: Number.POSITIVE_INFINITY,
    // Modest fishery when claimed (P3.8); unclaimed water yields nothing.
    foodRange: [1, 2],
    defenseMod: 0,
    passable: false,
    springFloodBlocksUnlessBridge: false,
    note: "Open water — no land march · modest food when claimed",
  },
};

export function tileStatsFor(biome: Biome): TileStatProfile {
  return TILE_STATS[biome];
}

/** Midpoint of food range (default yield when no RNG allocation). */
export function foodMidpoint(biome: Biome): number {
  const [lo, hi] = TILE_STATS[biome].foodRange;
  return (lo + hi) / 2;
}

/**
 * Effective movement cost to enter a cell.
 * Returns Infinity when blocked (water, or spring river without bridge).
 */
/**
 * P3.7 seasonal mountain pass: flagged mountain corridors thaw in Summer only.
 * Summer → foothills pace; other seasons → impassable snow (∞). Never settleable.
 */
export function isMountainPassThawed(season: Season, mountainPass?: boolean): boolean {
  return !!mountainPass && season === "Summer";
}

export function effectiveMoveCost(
  biome: Biome,
  season: Season,
  opts: { hasBridge?: boolean; mountainPass?: boolean } = {},
): number {
  // Seasonal pass: only open in Summer
  if (biome === "mountains" && opts.mountainPass) {
    if (season === "Summer") return TILE_STATS.foothills.moveCost;
    return Number.POSITIVE_INFINITY;
  }
  const s = TILE_STATS[biome];
  if (!s.passable || !Number.isFinite(s.moveCost)) {
    return Number.POSITIVE_INFINITY;
  }
  if (
    s.springFloodBlocksUnlessBridge &&
    season === "Spring" &&
    !opts.hasBridge
  ) {
    return Number.POSITIVE_INFINITY;
  }
  return s.moveCost;
}

/** Whether troops may enter this hex under current season / bridge. */
export function canEnterTile(
  biome: Biome,
  season: Season,
  opts: { hasBridge?: boolean; mountainPass?: boolean } = {},
): boolean {
  return Number.isFinite(effectiveMoveCost(biome, season, opts));
}

/**
 * Spring river flood rule — explicit helper for tests / HUD.
 * Non-river biomes always return false (not flood-blocked).
 */
export function isSpringRiverBlocked(
  biome: Biome,
  season: Season,
  hasBridge: boolean,
): boolean {
  if (biome !== "river") return false;
  if (season !== "Spring") return false;
  return !hasBridge;
}

/** Format defense mod for HUD: "+2", "−1", "0". */
export function formatDefenseMod(n: number): string {
  if (n > 0) return `+${n}`;
  if (n < 0) return `−${Math.abs(n)}`;
  return "0";
}

/** Format food range "3–5". */
export function formatFoodRange(biome: Biome): string {
  const [lo, hi] = TILE_STATS[biome].foodRange;
  if (lo === hi) return String(lo);
  return `${lo}–${hi}`;
}

/**
 * Wood yield when this tile is claimed (P3.7+ / playtest 0.3.11).
 * Midpoints of playtest ranges (same pattern as foodMidpoint):
 * forest 5–10 → 7.5; hills/foothills 1–2 → 1.5; else 0.
 * Shown as a single value so Selected can mirror Food’s “production” row.
 */
export function woodYield(biome: Biome): number {
  if (biome === "forest") return 7.5;
  if (biome === "hills" || biome === "foothills") return 1.5;
  return 0;
}

/** Format wood for Selected HUD: "7.5 (when claimed)" / "1.5 (when claimed)" / "0". */
export function formatWoodYield(biome: Biome): string {
  const n = woodYield(biome);
  if (n <= 0) return "0";
  return `${n} (when claimed)`;
}

/** Format move cost; ∞ when blocked. */
export function formatMoveCost(cost: number): string {
  if (!Number.isFinite(cost)) return "∞ (blocked)";
  return String(cost);
}

/** HUD blurb for seasonal mountain pass tiles. */
export function mountainPassNote(season: Season, mountainPass?: boolean): string | null {
  if (!mountainPass) return null;
  if (season === "Summer") {
    return "Mountain pass — summer thaw (foothills pace; not settleable)";
  }
  return "Mountain pass — snowbound / impassable until Summer (not settleable)";
}


/** GO anchor checks used by tests — mountain MP > plains; high-food set. */
export const HIGH_FOOD_BIOMES: readonly Biome[] = [
  "plains",
  "river",
  "coast",
] as const;

export function assertGoAnchors(): {
  mountainCostsMoreThanPlains: boolean;
  plainsFoodBeatsMountains: boolean;
  riverSpringNeedsBridge: boolean;
} {
  const mtn = TILE_STATS.mountains.moveCost;
  const pln = TILE_STATS.plains.moveCost;
  const plainsFood = foodMidpoint("plains");
  const mtnFood = foodMidpoint("mountains");
  return {
    mountainCostsMoreThanPlains: mtn > pln,
    plainsFoodBeatsMountains: plainsFood > mtnFood,
    riverSpringNeedsBridge: TILE_STATS.river.springFloodBlocksUnlessBridge,
  };
}
